import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Col, Row } from 'reactstrap';
import { Box, Container, CreateHeaders, HeaderSizeEnum, MenuItem, PrimaryButton, Select, SnackBarContext, booleanHeader, textHeader } from '@frontend-workspace/react-shared-lib';
import { useGetRequestNewHook, useMakeRequestHook, useTableConfigHook } from '../requestUtils';
import WebkuaTable from '../webkua-table/webkua-table';
import PageTitle from '../page-title/page-title';
import AutodeskCatalogVariantsExpand from '../autodesk-catalog-variants-expand/autodesk-catalog-variants-expand';
import AutodeskCurrencyRateEditor from '../autodesk-currency-rate-editor/autodesk-currency-rate-editor';
import FileDropzone from '../file-dropzone/file-dropzone';
import StatPills from '../stat-pills/stat-pills';

export interface IAutodeskSpecialProgram {
  orderAction: string;
  code: string;
  description: string;
  priceAfterDiscounts?: number;
}

/**
 * Mirrors the AutodeskCatalogProduct the API sends, field for field, rather
 * than modelling what the table shows -- which is why it is this long. The
 * sections say which half is actually read here.
 */
export interface IAutodeskCatalogProduct {
  // --- Read by this file ---

  offeringCode: string;
  offeringName: string;
  termDescription: string;
  servicePlanDescription: string;
  canSellNew: boolean;
  currency: string;
  /** Null on the base product. */
  specialProgramCode?: string;
  /** Null when the offering is not priced in bands. */
  fromQty?: number;
  toQty?: number;
  isVolumeBand: boolean;
  hasNewSale: boolean;
  hasRenewal: boolean;
  newSalePrice?: number;
  renewalPrice?: number;
  /** Converted at the company rate. These are the figures that get stored. */
  newSalePriceNok?: number;
  renewalPriceNok?: number;
  unknownOrderActionRowCount: number;
  specialPrograms: IAutodeskSpecialProgram[];
  alreadyImported: boolean;
  hasPriceMismatch: boolean;
  priceDifference?: number;
  isUnexpectedPair: boolean;
  isRenewalOnly: boolean;
  hasCollapsedVariants: boolean;
  /** Also the Visma article number for the new sale. */
  contractItemInfoId: string;
  renewalArticleNumber: string;
  priceNok?: number;
  /** Already stored, so drift against priceNok is visible. */
  importedPrice?: number;
  hasPriceDrift: boolean;
  blockedReason?: string;
  canImport: boolean;

  // --- Sent but not read here. Mostly the inputs the API already resolved
  // --- into a field above: the distinct* counts into hasCollapsedVariants,
  // --- the row counts into hasDuplicateBandRow, several into blockedReason.
  // --- Showing one instead re-derives a decision the API already made.

  offeringId: string;
  marketingName: string;
  specialization: string;
  termCode: string;
  accessModelDescription: string;
  servicePlanCode: string;
  lifeCycleState: string;
  /** Same rate for every row. The editor reads autodesk/currency instead. */
  eurToNokRate?: number;
  specialProgramDescription?: string;
  isSpecialProgramProduct: boolean;
  newSaleRowCount: number;
  renewalRowCount: number;
  isIncompletePair: boolean;
  distinctServicePlans: number;
  distinctAccessModels: number;
  distinctIntendedUsages: number;
  hasDuplicateBandRow: boolean;
  isSupportIncluded: boolean;
  relationProductId: string;
  hasIdCollision: boolean;
  invoiceInterval?: number;
  price?: number;
}

export interface IAutodeskImportOutcome {
  contractItemInfoId: string;
  productName: string;
  status: 'Created' | 'Updated' | 'Skipped';
  reason?: string;
}

export interface IAutodeskImportResult {
  products: IAutodeskImportOutcome[];
  relationProducts: IAutodeskImportOutcome[];
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
}

/** Mirrors the government DTO field for field -- see IAutodeskCatalogProduct. */
export interface IAutodeskGovernmentCatalogProduct {
  // --- Read by this file ---

  productDescription?: string;
  productLineCode?: string;
  contractTerm?: string;
  currency?: string;
  contractItemInfoId: string;
  /** A genuine second Synnex article, not a derived "-Ren" suffix like private's. */
  renewalContractItemInfoId?: string;
  hasNewSale: boolean;
  hasRenewal: boolean;
  isRenewalOnly: boolean;
  newSalePrice?: number;
  renewalPrice?: number;
  newSalePriceNok?: number;
  renewalPriceNok?: number;
  priceNok?: number;
  priceDifference?: number;
  /** Parsed from free text -- see AutodeskGovernmentCatalogProduct.Quantity for what it misses. */
  quantity?: number;
  /** Null on the ordinary renewal and on a new sale. */
  renewalVariant?: string;
  isRenewalVariantProduct: boolean;
  earnbackPercent: number;
  /** Lands on ContractItemInfo.CostPrice. */
  costPriceNok?: number;
  alreadyImported: boolean;
  importedPrice?: number;
  hasPriceDrift: boolean;
  hasPriceMismatch: boolean;
  blockedReason?: string;
  canImport: boolean;

  // --- Sent but not read here ---

  materialNumber: string;
  productLineDescription?: string;
  deployment?: string;
  licenseType?: string;
  /** Does not separate an ordinary renewal from an eligibility-priced one -- renewalVariant does. */
  materialType?: string;
  relationProductId: string;
  /** Same rate for every row. The editor reads autodesk/currency instead. */
  eurToNokRate?: number;
  price?: number;
}

export interface IAutodeskGovernmentCatalogResult {
  products: IAutodeskGovernmentCatalogProduct[];
  skippedRowCount: number;
}

function buildStatusText(
  product: { alreadyImported: boolean; hasPriceDrift: boolean; importedPrice?: number; priceNok?: number; blockedReason?: string },
  t: (key: string) => string
): string {
  if (!product.alreadyImported) return product.blockedReason || '';
  return product.hasPriceDrift
    ? `${t("autodeskCatalog.imported")} — ${t("autodeskCatalog.priceDrift")} ${product.importedPrice} → ${product.priceNok} NOK`
    : t("autodeskCatalog.imported");
}

interface IIssueCondition {
  active: boolean;
  message: string;
}

/** Shared by both segments; government currently passes an empty list. */
function evaluateIssues(conditions: IIssueCondition[]): { hasIssue: boolean; messages: string[] } {
  const messages = conditions.filter(c => c.active).map(c => c.message);
  return { hasIssue: messages.length > 0, messages };
}

/* eslint-disable-next-line */
export interface AutodeskCatalogProps { }

/** Browse the Autodesk catalogue and pick which products to create in Webkua. */
export function AutodeskCatalog(props: AutodeskCatalogProps) {
  const { t } = useTranslation();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const makeRequest = useMakeRequestHook();
  // Agency is the only type we are entitled to -- others return 48004. The
  // dropdown picks the segment, not this.
  const catalogType = 'Agency';
  const [segment, setSegment] = useState<'private' | 'government'>('private');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [creating, setCreating] = useState(false);
  const tableConfig = useTableConfigHook("autodeskCatalog");

  const { data, isLoading, getData }: { data: IAutodeskCatalogProduct[], isLoading: boolean, getData: () => void } =
    useGetRequestNewHook(`autodesk/catalog?catalogType=${catalogType}`, [], [catalogType]);

  // Separate from the catalogue so editing the rate doesn't wait on the slow read.
  const { data: currencyData, getData: getCurrencyData }: { data: { eurToNokRate?: number }, getData: () => void } =
    useGetRequestNewHook('autodesk/currency', {});

  // Kept because "create" resubmits it -- the server stages nothing.
  const [governmentExcelItem, setGovernmentExcelItem] = useState<string | undefined>(undefined);
  const [governmentData, setGovernmentData] = useState<IAutodeskGovernmentCatalogResult | undefined>(undefined);
  const [governmentLoading, setGovernmentLoading] = useState(false);
  const [governmentSelected, setGovernmentSelected] = useState<Record<string, boolean>>({});
  const [showTransitionPrices, setShowTransitionPrices] = useState(false);

  // Functional update, not a closure over `selected`: the headers useMemo below
  // needs this referentially stable.
  const toggleProduct = useCallback((key: string) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  }, []);

  const selectedCount = Object.keys(selected).length;

  const showImportResult = (result: IAutodeskImportResult) => {
    const parts = [
      result.createdCount > 0 ? `${result.createdCount} ${t("autodeskCatalog.created")}` : undefined,
      result.updatedCount > 0 ? `${result.updatedCount} ${t("autodeskCatalog.updated")}` : undefined,
      result.skippedCount > 0 ? `${result.skippedCount} ${t("autodeskCatalog.skipped")}` : undefined
    ].filter(Boolean);
    setSnackbarMessage({
      content: parts.join(', ') || t("autodeskCatalog.skipped"),
      type: (result.createdCount > 0 || result.updatedCount > 0) ? "success" : "warning"
    });
  };

  // updateExisting is always true: an already-imported product can be selected,
  // and selecting it means refresh, not create.
  const createProducts = async () => {
    setCreating(true);
    try {
      const result: IAutodeskImportResult = await makeRequest(
        'autodesk/catalog/action/createProducts', 'POST',
        { catalogType, contractItemInfoIds: Object.keys(selected), updateExisting: true });

      showImportResult(result);

      setSelected({});
      getData();
    } finally {
      setCreating(false);
    }
  };

  // Functional update, same reason as toggleProduct.
  const toggleGovernmentProduct = useCallback((key: string) => {
    setGovernmentSelected(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  }, []);

  const governmentSelectedCount = Object.keys(governmentSelected).length;

  const refreshGovernmentPreview = async (excelItem: string) => {
    setGovernmentLoading(true);
    try {
      const result: IAutodeskGovernmentCatalogResult = await makeRequest('autodesk/government/catalog', 'POST', excelItem);
      setGovernmentData(result);
    } finally {
      setGovernmentLoading(false);
    }
  };

  const onDropGovernmentPriceList = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const excelItem = reader.result as string;
      setGovernmentExcelItem(excelItem);
      setGovernmentSelected({});
      refreshGovernmentPreview(excelItem);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    getRootProps: getGovernmentDropzoneRootProps,
    getInputProps: getGovernmentDropzoneInputProps,
    isDragActive: isGovernmentDropzoneActive
  } = useDropzone({
    onDrop: onDropGovernmentPriceList,
    // react-dropzone 11.x: accept is a flat list, not the object-map form later versions use.
    accept: ['.xlsx', '.xls', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'],
    multiple: false
  });

  const createGovernmentProducts = async () => {
    if (!governmentExcelItem) return;
    setCreating(true);
    try {
      const result: IAutodeskImportResult = await makeRequest(
        'autodesk/government/catalog/action/createProducts', 'POST',
        { excelItem: governmentExcelItem, contractItemInfoIds: Object.keys(governmentSelected), updateExisting: true });

      showImportResult(result);

      setGovernmentSelected({});
      await refreshGovernmentPreview(governmentExcelItem);
    } finally {
      setCreating(false);
    }
  };

  const products = data || [];
  const mismatchCount = products.filter(p => p.hasPriceMismatch).length;
  const renewalOnlyCount = products.filter(p => p.isRenewalOnly).length;
  const importedCount = products.filter(p => p.alreadyImported).length;
  const blockedCount = products.filter(p => !p.alreadyImported && !p.canImport).length;
  const driftCount = products.filter(p => p.hasPriceDrift).length;

  const rows = products.map(product => {
    // Private only -- see GetAutodeskCatalogCH for what triggers each.
    const { hasIssue, messages: issueMessages } = evaluateIssues([
      { active: product.hasCollapsedVariants, message: t("autodeskCatalog.collapsedVariantsMessage") },
      { active: product.unknownOrderActionRowCount > 0, message: t("autodeskCatalog.unknownOrderActionMessage") },
      { active: product.isUnexpectedPair, message: t("autodeskCatalog.unexpectedPairMessage") }
    ]);
    return {
      ...product,
      product: product.offeringName,
      code: product.offeringCode,
      discountProgram: product.specialProgramCode || '',
      setupId: product.hasNewSale ? product.contractItemInfoId : '',
      recurringId: product.hasRenewal ? product.renewalArticleNumber : '',
      term: product.termDescription,
      newSaleText: product.newSalePrice != null ? `${product.newSalePrice} ${product.currency}` : '',
      renewalText: product.renewalPrice != null ? `${product.renewalPrice} ${product.currency}` : '',
      newSaleNokText: product.newSalePriceNok != null ? `${product.newSalePriceNok} NOK` : '',
      renewalNokText: product.renewalPriceNok != null ? `${product.renewalPriceNok} NOK` : '',
      priceDiffText: product.hasPriceMismatch ? `${product.priceDifference} ${product.currency}` : '',
      quantityText: product.isVolumeBand
        ? (product.toQty != null ? `${product.fromQty}-${product.toQty}` : `${product.fromQty}+`)
        : '',
      programCount: product.specialPrograms.length,
      // Government-only columns.
      earnbackText: '',
      costPriceText: '',
      // selectRenderer reads this instead of closing over `selected`.
      isSelectedForImport: !!selected[product.contractItemInfoId],
      hasIssue,
      status: [buildStatusText(product, t), ...issueMessages].filter(Boolean).join(' — ')
    };
  });

  const governmentProducts = governmentData?.products || [];
  const governmentImportedCount = governmentProducts.filter(p => p.alreadyImported).length;
  const governmentBlockedCount = governmentProducts.filter(p => !p.alreadyImported && !p.canImport).length;
  const governmentMismatchCount = governmentProducts.filter(p => p.hasPriceMismatch).length;
  const governmentDriftCount = governmentProducts.filter(p => p.hasPriceDrift).length;
  const governmentRenewalOnlyCount = governmentProducts.filter(p => p.isRenewalOnly).length;
  const governmentVariantCount = governmentProducts.filter(p => p.isRenewalVariantProduct).length;

  // Mapped onto the private rows' field names so one header set serves both.
  const governmentRows = governmentProducts.map(product => {
    const { hasIssue, messages: issueMessages } = evaluateIssues([]);
    return {
      ...product,
      product: product.productDescription,
      code: product.productLineCode,
      // Stands in for private's programme code: without it an eligibility-priced
      // renewal is indistinguishable from the ordinary one.
      discountProgram: product.renewalVariant || '',
      // A renewal-only row is its own recurring article with no setup side, which
      // is why these look "swapped" -- contractItemInfoId stays the document id.
      setupId: product.hasNewSale ? product.contractItemInfoId : '',
      recurringId: product.hasRenewal
        ? (product.renewalContractItemInfoId || product.contractItemInfoId)
        : '',
      term: product.contractTerm,
      quantityText: product.quantity != null ? String(product.quantity) : '',
      newSaleText: product.newSalePrice != null ? `${product.newSalePrice} ${product.currency}` : '',
      renewalText: product.renewalPrice != null ? `${product.renewalPrice} ${product.currency}` : '',
      newSaleNokText: product.newSalePriceNok != null ? `${product.newSalePriceNok} NOK` : '',
      renewalNokText: product.renewalPriceNok != null ? `${product.renewalPriceNok} NOK` : '',
      priceDiffText: product.hasPriceMismatch ? `${product.priceDifference} ${product.currency}` : '',
      servicePlanDescription: '',
      programCount: 0,
      earnbackText: `${product.earnbackPercent} %`,
      costPriceText: product.costPriceNok != null ? `${product.costPriceNok} NOK` : '',
      canSellNew: product.hasNewSale,
      isSelectedForImport: !!governmentSelected[product.contractItemInfoId],
      hasIssue,
      status: [buildStatusText(product, t), ...issueMessages].filter(Boolean).join(' — ')
    };
  });

  // An already-imported transition price stays visible whatever the filter says.
  const visibleGovernmentRows = showTransitionPrices
    ? governmentRows
    : governmentRows.filter(r => !r.isRenewalVariantProduct || r.alreadyImported);

  const tableRows = segment === 'private' ? rows : visibleGovernmentRows;
  const tableIsLoading = segment === 'private' ? isLoading : governmentLoading;
  const onRefreshTable = segment === 'private'
    ? getData
    : (governmentExcelItem ? () => refreshGovernmentPreview(governmentExcelItem) : undefined);

  const tableFilters = segment === 'government'
    ? [{
        label: t("autodeskCatalog.showTransitionPrices"),
        checked: showTransitionPrices,
        onChange: (checked: boolean) => setShowTransitionPrices(checked)
      }]
    : undefined;

  // Reads isSelectedForImport off the row, not the selection maps, so this stays
  // referentially stable -- the headers memo below depends on it.
  const selectRenderer = useCallback((params: any) => {
    const product = params.data;
    if (!product.alreadyImported && !product.canImport) return null;
    const toggle = segment === 'private' ? toggleProduct : toggleGovernmentProduct;
    const isSelected = !!product.isSelectedForImport;
    const label = isSelected
      ? t("autodeskCatalog.selected")
      : (product.alreadyImported ? t("autodeskCatalog.update") : t("autodeskCatalog.select"));
    return (
      <PrimaryButton
        variant={isSelected ? 'contained' : 'outlined'}
        size='small'
        onClick={() => toggle(product.contractItemInfoId)}
      >
        {label}
      </PrimaryButton>
    );
  }, [segment, toggleProduct, toggleGovernmentProduct, t]);

  // Must stay memoised -- see AutodeskCurrencyRateEditor.
  const headers = useMemo(() => CreateHeaders([
    textHeader("product", t("autodeskCatalog.product"), HeaderSizeEnum.EXTRA_LARGE, {
      renderer: segment === 'private' ? 'agGroupCellRenderer' : undefined,
      cellStyle: { fontSize: '0.875rem', fontWeight: '600' }
    }),
    textHeader("code", t("autodeskCatalog.code"), HeaderSizeEnum.MEDIUM),
    textHeader("discountProgram", t("autodeskCatalog.discountProgram"), HeaderSizeEnum.MEDIUM),
    textHeader("setupId", t("autodeskCatalog.setupId"), HeaderSizeEnum.LARGE),
    textHeader("recurringId", t("autodeskCatalog.recurringId"), HeaderSizeEnum.LARGE),
    textHeader("term", t("autodeskCatalog.term"), HeaderSizeEnum.MEDIUM),
    textHeader("quantityText", t("autodeskCatalog.quantity"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("newSaleText", t("autodeskCatalog.newSaleEur"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("newSaleNokText", t("autodeskCatalog.newSaleNok"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("renewalText", t("autodeskCatalog.renewalEur"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("renewalNokText", t("autodeskCatalog.renewalNok"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("priceDiffText", t("autodeskCatalog.priceDifference"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("earnbackText", t("autodeskCatalog.earnback"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("costPriceText", t("autodeskCatalog.costPrice"), HeaderSizeEnum.MEDIUM, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    booleanHeader("isRenewalOnly", t("autodeskCatalog.renewalOnly")),
    textHeader("servicePlanDescription", t("autodeskCatalog.servicePlan"), HeaderSizeEnum.LARGE, { hide: true }),
    textHeader("programCount", t("autodeskCatalog.programs"), HeaderSizeEnum.SMALL, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    booleanHeader("canSellNew", t("autodeskCatalog.sellable")),
    booleanHeader("hasIssue", t("autodeskCatalog.hasIssue")),
    textHeader("status", t("autodeskCatalog.status"), HeaderSizeEnum.MEDIUM),
    textHeader("", "", HeaderSizeEnum.MEDIUM, {
      renderer: selectRenderer,
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ]), [segment, t, selectRenderer]);

  const privateStats = [
    { label: t("autodeskCatalog.imported"), value: importedCount },
    { label: t("autodeskCatalog.blocked"), value: blockedCount },
    { label: t("autodeskCatalog.priceDrift"), value: driftCount },
    { label: t("autodeskCatalog.priceMismatches"), value: mismatchCount },
    { label: t("autodeskCatalog.renewalOnly"), value: renewalOnlyCount }
  ];
  const governmentStats = [
    { label: t("autodeskCatalog.imported"), value: governmentImportedCount },
    { label: t("autodeskCatalog.blocked"), value: governmentBlockedCount },
    { label: t("autodeskCatalog.priceDrift"), value: governmentDriftCount },
    { label: t("autodeskCatalog.priceMismatches"), value: governmentMismatchCount },
    { label: t("autodeskCatalog.renewalOnly"), value: governmentRenewalOnlyCount },
    { label: t("autodeskCatalog.renewalVariants"), value: governmentVariantCount }
  ];

  return (
    <Container
      className="container-fluid pt-4 px-0"
      maxWidth={false}
      sx={{ paddingBottom: '50px' }}>
      <Row>
        <Col>
          <Box component="div" className="pb-5">
            <PageTitle title={t("autodeskCatalog.title")} subtitle={`${t("autodeskCatalog.subtitle")}`} />

            <Box className="d-flex align-items-center gap-2 mb-3">
              <Select
                size="small"
                value={segment}
                onChange={(e: any) => setSegment(e.target.value)}
              >
                <MenuItem value="private">{t("autodeskCatalog.private")}</MenuItem>
                <MenuItem value="government">{t("autodeskCatalog.government")}</MenuItem>
              </Select>
            </Box>

            <AutodeskCurrencyRateEditor
              eurToNokRate={currencyData?.eurToNokRate}
              onSaved={() => { getCurrencyData(); getData(); if (governmentExcelItem) refreshGovernmentPreview(governmentExcelItem); }}
            />

            {segment === 'private' && (<>
            <Box className="d-flex align-items-center gap-3 mb-3 flex-wrap">
              <span>{products.length} {t("autodeskCatalog.products")}</span>
              <StatPills stats={privateStats} />

              <Box className="ms-auto d-flex gap-2">
                <PrimaryButton
                  variant="contained"
                  disabled={selectedCount === 0 || creating}
                  onClick={createProducts}
                >
                  {t("autodeskCatalog.createProducts")} ({selectedCount})
                </PrimaryButton>
              </Box>
            </Box>
            </>)}

            {segment === 'government' && (<>
            <FileDropzone
              getRootProps={getGovernmentDropzoneRootProps}
              getInputProps={getGovernmentDropzoneInputProps}
              isDragActive={isGovernmentDropzoneActive}
              label={t("autodeskCatalog.uploadPriceList")}
              hint={t("autodeskCatalog.dropOrClick")}
            />

            <Box className="d-flex align-items-center gap-3 mb-3 flex-wrap">
              <span>
                {visibleGovernmentRows.length !== governmentProducts.length
                  ? `${visibleGovernmentRows.length} / ${governmentProducts.length}`
                  : governmentProducts.length} {t("autodeskCatalog.products")}
              </span>
              <StatPills stats={governmentStats} />
              {governmentData && governmentData.skippedRowCount > 0 && (
                <span>{t("autodeskCatalog.skippedRows")}: <strong>{governmentData.skippedRowCount}</strong></span>
              )}

              <Box className="ms-auto d-flex gap-2">
                <PrimaryButton
                  variant="contained"
                  disabled={governmentSelectedCount === 0 || creating}
                  onClick={createGovernmentProducts}
                >
                  {t("autodeskCatalog.createProducts")} ({governmentSelectedCount})
                </PrimaryButton>
              </Box>
            </Box>
            </>)}

            {/*
              One table, one column set, for both segments -- product/setup id/
              recurring id/term/EUR+NOK price/price difference/sellable/status mean
              the same thing on both sides (see the rows/governmentRows mapping
              above). Fields with no government equivalent are simply blank there;
              nothing is hidden per segment, since it is one shared table.
            */}
            <WebkuaTable
              data={tableRows}
              isLoading={tableIsLoading}
              onRefresh={onRefreshTable}
              tableConfig={tableConfig}
              tableFilters={tableFilters}
              expandComponent={segment === 'private' ? AutodeskCatalogVariantsExpand : undefined}
              pageSize={25}
              headers={headers}
            />
          </Box>
        </Col>
      </Row>
    </Container>
  );
}

export default AutodeskCatalog;
